import { AsyncPipe, LowerCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { Router, RouterModule } from '@angular/router';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { CancellationReasonEnum, SubscriptionCancellationCompetitorEnum } from '@rma/billing-api';
import { PauseSubscriptionService } from '@rma/dashboard/billing/my-subscription';
import { CurrentSubscriptionService } from '@rma/dashboard/billing/subscription-plans/public/api-subscription-features';
import { RmaLetModule } from '@rma/dashboard/generic/ui/let';
import { FeatureFlags, FeatureFlagService } from '@rma/dashboard/generic/util/feature-flags';
import { NavigationHeaderComponent } from '@rma/dashboard/layout/public/navigation-header';
import { fromControl } from '@rma/generic/rxjs/operators/from-control';
import { combineLatest, map, Observable, take } from 'rxjs';
import { feedbackValidator } from '../domain/feedback.validator';
import { UnSubForm } from '../domain/unsub-form';
import { CancellationReasonService } from './../cancellation-reason.service';

const scope = 'subscriptions';

@Component({
  selector: 'rma-unsubscribe-confirm',
  templateUrl: './unsubscribe-confirm.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    LowerCasePipe,
    MatButtonModule,
    ReactiveFormsModule,
    TranslocoModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatSelectModule,
    NavigationHeaderComponent,
    RmaLetModule,
    RouterModule,
  ],
  providers: [provideTranslocoScope(scope)],
})
export class UnsubscribeConfirmComponent {
  private readonly subscriptionService = inject(CurrentSubscriptionService);
  private readonly router = inject(Router);
  private readonly cancellationReasonService = inject(CancellationReasonService);
  private readonly showPauseSubscription$ = inject(PauseSubscriptionService).showPauseSubscription$;
  private readonly featureService = inject(FeatureFlagService);

  protected readonly scope = `${scope}.billing.unsubscribe`;

  protected reasons$: Observable<CancellationReasonEnum[]>;
  protected displayOtherReason$: Observable<boolean>;
  protected displayCompetitors$: Observable<boolean>;
  protected displayOtherTool$: Observable<boolean> | undefined;

  protected unSubForm: FormGroup<UnSubForm>;
  protected competitors$: Observable<SubscriptionCancellationCompetitorEnum[]>;

  constructor() {
    this.unSubForm = new FormGroup<UnSubForm>(
      {
        cancellationReason: new FormControl(null, {
          nonNullable: true,
          validators: [Validators.required],
        }),
        cancellationFeedback: new FormControl<undefined | string>(undefined, {
          nonNullable: true,
        }),
        cancellationCompetitor: new FormControl(null, {
          nonNullable: true,
        }),
        otherTool: new FormControl<undefined | string>(undefined, {
          nonNullable: true,
        }),
      },
      {
        validators: [feedbackValidator, this.otherToolValidator, this.competitorValidator],
      },
    );

    this.reasons$ = this.subscriptionService.cancellationReasons$;
    this.competitors$ = this.subscriptionService.cancellationCompetitors$;
    this.displayOtherReason$ = fromControl(this.unSubForm.controls.cancellationReason).pipe(
      map((reason) => {
        if (reason !== CancellationReasonEnum.AnotherReviewPlatform) {
          this.unSubForm.controls.cancellationCompetitor?.setValue(null);
          this.unSubForm.controls.otherTool?.setValue(undefined);
        }
        return reason === CancellationReasonEnum.Other;
      }),
    );
    this.displayCompetitors$ = fromControl(this.unSubForm.controls.cancellationReason).pipe(
      map((reason) => reason === CancellationReasonEnum.AnotherReviewPlatform),
    );
    this.displayOtherTool$ = this.unSubForm.controls.cancellationCompetitor?.valueChanges.pipe(
      map((competitor) => {
        return competitor === SubscriptionCancellationCompetitorEnum.Other;
      }),
    );
  }

  onSubmit() {
    const formValue = this.unSubForm.value;

    if (formValue.cancellationFeedback) {
      this.cancellationReasonService.setCancellationFeedback(formValue.cancellationFeedback);
    }

    if (formValue.cancellationReason === CancellationReasonEnum.AnotherReviewPlatform && formValue.cancellationCompetitor) {
      this.cancellationReasonService.setCancellationCompetitor(formValue.cancellationCompetitor);
      if (formValue.cancellationCompetitor === SubscriptionCancellationCompetitorEnum.Other && formValue.otherTool) {
        this.cancellationReasonService.setOtherTool(formValue.otherTool);
      }
    }

    combineLatest([
      this.featureService.getFeature$(FeatureFlags.CallBeforeUnsubscribe, false),
      this.showPauseSubscription$,
      this.subscriptionService.currentSubscription$.pipe(map((sub) => sub.onTrial)),
    ])
      .pipe(take(1))
      .subscribe(([callBeforeUnsubscribe, showPauseSubscription, onTrial]) => {
        if (callBeforeUnsubscribe && !onTrial && formValue.cancellationReason) {
          //TODO: Do not nest a subscribe in a subscribe. Move the logic up into the operators
          //TODO: I don't have time/context to fix this but the next person working here should refactor this.
          this.subscriptionService
            .postSubscriptionCancellationReason(
              [formValue.cancellationReason],
              formValue.cancellationFeedback,
              formValue.cancellationCompetitor ? formValue.cancellationCompetitor : undefined,
              formValue.otherTool,
            )
            .subscribe({
              next: () => {
                this.router.navigateByUrl('/billing/call-unsubscribe');
              },
              error: (err) => {
                console.error('An error occurred during the cancellation reason post', err);
              },
            });
        } else {
          if (formValue.cancellationReason) {
            this.cancellationReasonService.setCancellationReason(formValue.cancellationReason);
            this.navigateBasedOnReason(formValue.cancellationReason, showPauseSubscription);
          }
        }
      });
  }

  private navigateBasedOnReason(reason: CancellationReasonEnum, showPause: boolean) {
    const navigationMap: { [key in CancellationReasonEnum]?: string } = {
      [CancellationReasonEnum.TooExpensive]: '/billing/unsubscribe-eoi',
      [CancellationReasonEnum.TemporaryBreak]: showPause ? '/billing/pause' : '/billing/unsubscribe-confirm',
      [CancellationReasonEnum.Complicated]: '/billing/unsubscribe-support',
      [CancellationReasonEnum.BugsAndIssues]: '/billing/unsubscribe-support',
      [CancellationReasonEnum.DataIssues]: '/billing/unsubscribe-support',
    };

    this.router.navigateByUrl(navigationMap[reason] || '/billing/unsubscribe-confirm');
  }

  private otherToolValidator(control: AbstractControl) {
    const competitor = control.get('cancellationCompetitor')?.value;
    const otherTool = control.get('otherTool')?.value;

    return competitor === SubscriptionCancellationCompetitorEnum.Other && (!otherTool || otherTool.trim().length === 0)
      ? { otherToolRequired: true }
      : null;
  }

  private competitorValidator(control: AbstractControl) {
    const competitor = control.get('cancellationCompetitor')?.value;
    const reason = control.get('cancellationReason')?.value;

    return reason === CancellationReasonEnum.AnotherReviewPlatform && !competitor ? { cancellationCompetitorRequired: true } : null;
  }
}
